import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '~/services/auth';
import { useSyncUser } from '~/hooks/models/useUser';
import { useSyncClients } from '~/hooks/models/useClient';
import { useGames } from '~/hooks/models/useGame';
import { useTick } from '~/hooks/useTick';
import { useAnimatorLanguage } from '~/hooks/useAnimatorLanguage';
import { Mode } from '~/types/client';
import { LoadingPage } from '~/pages/LoadingPage';

import { AnimatorInstallBanner } from './AnimatorLayout';
import styles from './Animator.module.css';

function AnimatorListContent({ clientIDs }: { clientIDs: string[] }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const clients = useSyncClients(clientIDs);
    // Réévalue isLive() toutes les 30s : éteindre une TV n'écrit rien en base, donc
    // sans ce tick la liste resterait « LIVE » faute de nouveau snapshot.
    useTick(30_000);

    // IDs des jeux en cours sur chaque écran → libellés (un seul fetch, mémoïsé
    // pour éviter de re-souscrire à chaque render — cf gotcha useData).
    const gameIDs = useMemo(
        () =>
            Array.from(
                new Set((clients || []).map((c) => c.getScreen(Mode.Anim)?.gameID).filter((id): id is string => !!id))
            ),
        [clients]
    );
    // Langue de la liste : celle des établissements quand ils s'accordent (le cas
    // courant — un animateur, une enseigne). Sinon on garde la langue déjà en place
    // (navigateur), et chaque cockpit se recalera sur son propre établissement.
    const commonLanguage = useMemo(() => {
        const langs = new Set((clients || []).map((c) => c.language).filter(Boolean));
        return langs.size === 1 ? [...langs][0] : undefined;
    }, [clients]);
    useAnimatorLanguage(commonLanguage);

    const games = useGames(gameIDs);
    const gameLabel = useMemo(() => {
        const map: Record<string, string> = {};
        (games || []).forEach((g) => {
            map[g.id] = g.name;
        });
        return map;
    }, [games]);

    if (!clients) {
        return <LoadingPage>...</LoadingPage>;
    }

    const rows = clients
        .map((client) => {
            const screen = client.getScreen(Mode.Anim);
            return {
                id: client.id,
                name: client.name,
                live: screen?.isLive() ?? false,
                gameID: screen?.gameID,
                playerCount: screen?.playerCount ?? 0,
            };
        })
        .sort((a, b) => Number(b.live) - Number(a.live) || a.name.localeCompare(b.name));

    return (
        <div className={styles.page}>
            <div className={styles.shell}>
                <header className={styles.header}>
                    <div>
                        <p className={styles.kicker}>{t('animator.list.kicker')}</p>
                        <h1 className={styles.title}>{t('animator.list.title')}</h1>
                    </div>
                </header>

                <AnimatorInstallBanner />

                <div className={styles.list}>
                    {rows.map((row) => (
                        <button
                            key={row.id}
                            type="button"
                            className={[styles.estBtn, row.live ? styles.estLiveBorder : styles.estOff].join(' ')}
                            onClick={() => navigate(`/animator/${row.id}`)}
                        >
                            <div className={styles.estTop}>
                                <span className={styles.estName}>{row.name}</span>
                                {row.live ? (
                                    <span className={`${styles.pill} ${styles.pillLive}`}>
                                        <span className={styles.dot} />
                                        {t('animator.list.live')}
                                    </span>
                                ) : (
                                    <span className={`${styles.pill} ${styles.pillOff}`}>
                                        <span className={styles.dot} />
                                        {t('animator.list.off')}
                                    </span>
                                )}
                            </div>
                            {row.live && (
                                <div className={styles.estMeta}>
                                    <span>
                                        {row.gameID ? gameLabel[row.gameID] || row.gameID : t('animator.list.homeMenu')}
                                    </span>
                                    <span className={styles.metaSep}>•</span>
                                    <span>{t('animator.list.players', { count: row.playerCount })}</span>
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

/**
 * Animator Control — écran 1 : la liste des établissements dont l'animateur est
 * membre, avec leur état en direct (LIVE / éteint, jeu en cours, joueurs).
 *
 * Découverte par compte (cf décision MVP) : appartenance via users/{uid}/sites
 * (getSiteIDs), état live lu sur screenInfos.anim (un écran par établissement).
 * Aucune connexion socket ici — pure lecture Firestore.
 */
export function AnimatorList() {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const user = useSyncUser(auth?.uid || 'unknown');

    if (!user) {
        return <LoadingPage>{t('home.loadingData')}</LoadingPage>;
    }

    const siteIDs = (user.getSiteIDs() || []).filter((id) => id !== '*' && id !== 'demo');

    if (siteIDs.length === 0) {
        return (
            <div className={styles.page}>
                <div className={styles.shell}>
                    <div className={styles.center}>
                        <p className={styles.title}>{t('animator.list.emptyTitle')}</p>
                        <p className={styles.muted}>{t('animator.list.emptyBody')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return <AnimatorListContent clientIDs={siteIDs} />;
}
